// An area's photos as one list: its photo rows (photo-rows.ts, one per photo,
// from any device), then — while it still exists — the area's older hig_photos
// list for photos uploaded before the rows, and the photos on this device,
// waiting or sent. A photo in more than one is shown once. Removed photos
// (in CRM, or on this device waiting to be sent) never show.
import type { DevicePhoto, DeviceRecord } from './device-db';
import { photoRowsOf, photoRowsOfArea, removingIds, rowFacts } from './photo-rows.ts';

/** One entry of hig_measurearea.hig_photos, as far as the app reads it (the API's PhotoList). */
export interface PhotoListEntry {
  photoId: string;
  surfaceId?: string;
  name: string;
  direction?: string;
  bearing?: number;
  /** Where it was taken; absent = no fix, or taken before 2026-10-21. */
  latitude?: number;
  longitude?: number;
  accuracy?: number;
  uploadedOn?: string;
  /** The file on the job's SharePoint site: …/Photo - Before/<area name>/<file>. */
  webUrl?: string;
  /** The area's own perspective shot (#437); the API keeps it on one entry, the latest sent. */
  areaPhoto?: boolean;
}

/** A photo as an area screen shows it. `device` is null for one taken on another device; `row` null for one from before the rows. */
export interface ShownPhoto {
  id: string;
  areaId: string;
  surfaceId: string | null;
  fileName: string;
  direction: string | null;
  uploaded: boolean;
  /** The area's feature photo (#437): its row's purpose, else the older list's mark, else the device's. */
  areaPhoto: boolean;
  /** SharePoint said the file is gone (its row's File missing since). */
  missing: boolean;
  /** When it was taken, as far as known (the older list only knows when it uploaded). */
  takenOn: string | null;
  device: DevicePhoto | null;
  row: DeviceRecord | null;
}

/** Where an area's photos are read from. */
export interface PhotoSources {
  /** The job's records: its photo rows are among them. */
  records: readonly DeviceRecord[];
  /** This device's photos. */
  onDevice: readonly DevicePhoto[];
  /** Photos removed in CRM (the job's removedPhotoIds). */
  removed?: readonly string[];
}

/** The entries of an area's hig_photos; none when it's empty or unreadable. */
export function uploadedList(raw: unknown): PhotoListEntry[] {
  if (typeof raw !== 'string' || !raw) return [];
  try {
    const parsed = JSON.parse(raw) as { photos?: PhotoListEntry[] };
    return Array.isArray(parsed.photos) ? parsed.photos : [];
  } catch {
    return [];
  }
}

/**
 * The direction an area's hig_photos has for an uploaded photo (null: listed
 * with none); undefined when the photo isn't on the list at all.
 */
export function listedDirection(raw: unknown, photoId: string): string | null | undefined {
  const entry = uploadedList(raw).find((p) => p.photoId === photoId);
  return entry ? (entry.direction ?? null) : undefined;
}

/**
 * The area's hig_photos with a photo this device just uploaded added once, so
 * the device's copy shows it (and its folder) before the job is next opened.
 * CRM's list replaces this copy when the job refreshes. Only for a photo with
 * no row (the API recorded it on the list).
 */
export function withUploadedPhoto(raw: unknown, entry: PhotoListEntry): string {
  let list: { photos?: unknown } = {};
  try {
    if (typeof raw === 'string' && raw) list = JSON.parse(raw) as { photos?: unknown };
  } catch {
    // Unreadable: start a fresh list.
  }
  const photos = uploadedList(raw);
  if (photos.some((p) => p.photoId === entry.photoId)) return JSON.stringify({ ...list, photos });
  // As the API does: a new area photo takes the mark from the one before.
  const others = entry.areaPhoto ? photos.map(({ areaPhoto: _, ...p }) => p) : photos;
  return JSON.stringify({ ...list, photos: [...others, entry] });
}

const byName = (a: ShownPhoto, b: ShownPhoto) => a.fileName.localeCompare(b.fileName, undefined, { numeric: true });

/** Every photo of the area, once each, in name order (Walls 01, Walls 02 … General 01). */
export function shownPhotos(area: { id: string; fields: Record<string, unknown> }, sources: PhotoSources): ShownPhoto[] {
  const hidden = new Set([...(sources.removed ?? []), ...removingIds(sources.records)]);
  const shown = new Map<string, ShownPhoto>();
  for (const p of uploadedList(area.fields.hig_photos)) {
    shown.set(p.photoId, {
      id: p.photoId,
      areaId: area.id,
      surfaceId: p.surfaceId ?? null,
      fileName: p.name,
      direction: p.direction ?? null,
      uploaded: true,
      areaPhoto: !!p.areaPhoto,
      missing: false,
      takenOn: p.uploadedOn ?? null,
      device: null,
      row: null,
    });
  }
  // A row is the photo's record: it wins over the older list.
  for (const row of photoRowsOfArea(sources.records, area.id)) {
    const facts = rowFacts(row);
    shown.set(row.id, { id: row.id, areaId: area.id, ...facts, areaPhoto: facts.featurePhoto, uploaded: facts.uploaded || shown.has(row.id), device: null, row });
  }
  for (const p of sources.onDevice.filter((d) => d.areaId === area.id)) {
    const known = shown.get(p.id);
    const listedMark = known && !known.row ? known.areaPhoto : undefined;
    shown.set(p.id, {
      id: p.id,
      areaId: area.id,
      surfaceId: known?.surfaceId ?? p.surfaceId ?? null,
      fileName: known?.row ? known.fileName : p.fileName,
      // The row holds the direction; before the rows, this device's own may be newer than the list's.
      direction: known?.row ? known.direction : (p.direction ?? null),
      uploaded: !!known?.uploaded || p.state === 'uploaded',
      // Once on a row or the list, CRM's mark wins: a later area photo from another device has taken it.
      areaPhoto: known?.row ? known.areaPhoto : (listedMark ?? !!p.areaPhoto),
      missing: known?.missing ?? false,
      takenOn: known?.row ? known.takenOn : p.takenAt,
      device: p,
      row: known?.row ?? null,
    });
  }
  return [...shown.values()].filter((p) => !hidden.has(p.id)).sort(byName);
}

/**
 * The area's own photo (#437), or null while it has none. One still waiting on
 * this device is newer than CRM's (the latest taken, if several); else the
 * latest taken of those marked (CRM keeps one; two may show until it settles).
 */
export function areaPhotoOf(photos: readonly ShownPhoto[]): ShownPhoto | null {
  const latest = (list: ShownPhoto[]) => [...list].sort((a, b) => (a.takenOn ?? '').localeCompare(b.takenOn ?? '')).at(-1) ?? null;
  const marked = photos.filter((p) => p.areaPhoto);
  return latest(marked.filter((p) => !p.uploaded && p.device)) ?? latest(marked);
}

/** The folder every area's photo folder sits in, on the job's SharePoint site. */
const PHOTO_FOLDER = 'Photo - Before';

/** Every uploaded photo's link on the job: its rows, then the areas' older lists. */
export function photoLinks(records: readonly DeviceRecord[]): string[] {
  const fromRows = photoRowsOf(records).map((r) => rowFacts(r).webUrl);
  const fromLists = records.filter((r) => r.table === 'area').flatMap((a) => uploadedList(a.fields.hig_photos).map((p) => p.webUrl));
  return [...fromRows, ...fromLists].filter((url): url is string => !!url);
}

/**
 * The job's "Photo - Before" folder on SharePoint, from any uploaded photo's
 * own link (two steps up: past the file and its area's folder), so the
 * library's path is read, never assumed. Null until a photo has uploaded.
 */
export function photoFolderUrl(links: readonly string[]): string | null {
  for (const link of links) {
    try {
      const url = new URL(link);
      const parts = url.pathname.split('/');
      const folder = parts.slice(0, -2);
      if (decodeURIComponent(folder.at(-1) ?? '') !== PHOTO_FOLDER) continue;
      return `${url.origin}${folder.join('/')}`;
    } catch {
      continue;
    }
  }
  return null;
}

/** Every file name the area's photos already use, on any device, so a new photo never takes one. */
export function takenNames(area: { id: string; fields: Record<string, unknown> }, sources: PhotoSources): string[] {
  const rows = photoRowsOfArea(sources.records, area.id).map((r) => rowFacts(r).fileName);
  return [...uploadedList(area.fields.hig_photos).map((p) => p.name), ...rows, ...sources.onDevice.map((p) => p.fileName)];
}
